import type { TechId } from './ids';
import type { TradeGoodId } from './market';

/**
 * Exploring dust clouds (GDD §7). Explorers scan a cloud find by find; each
 * find is drawn from a per-level bag shuffled for the match, so there are
 * no long runs of the same result. Starting values for tuning.
 */
export const FIND_IDS = ['cache', 'derelict', 'anomaly', 'asteroid', 'pirates'] as const;
export type FindId = (typeof FIND_IDS)[number];

export type CloudLevelId = 1 | 2 | 3;

export interface CloudLevelDef {
  /** Finds a cloud of this level holds. */
  readonly finds: number;
  /** Ticks to scan one find (GDD §7: 30 / 60 / 90 s). */
  readonly scanTicks: number;
  /** Technology the explorer's owner needs (#27); null = any explorer. */
  readonly requiresTech: TechId | null;
  /** The bag: how many of each find it holds. Refilled and reshuffled when empty. */
  readonly bag: Readonly<Record<FindId, number>>;
}

export const CLOUD_LEVELS: Readonly<Record<CloudLevelId, CloudLevelDef>> = {
  1: {
    finds: 2,
    scanTicks: 300,
    requiresTech: null,
    bag: { cache: 4, derelict: 2, anomaly: 2, asteroid: 2, pirates: 0 },
  },
  2: {
    finds: 4,
    scanTicks: 600,
    requiresTech: 'scannerMk2',
    bag: { cache: 3, derelict: 3, anomaly: 2, asteroid: 2, pirates: 2 },
  },
  3: {
    finds: 6,
    scanTicks: 900,
    requiresTech: 'scannerMk3',
    bag: { cache: 3, derelict: 4, anomaly: 3, asteroid: 2, pirates: 4 },
  },
};

/** What a find gives, per cloud level (multiplied by the level). */
export const FINDS = {
  /** A cache: one of these, picked by the match RNG. */
  cache: [
    { good: 'metals', amount: 40 },
    { good: 'helium3', amount: 30 },
    { good: 'exotics', amount: 5 },
  ] as readonly { readonly good: TradeGoodId; readonly amount: number }[],
  /** A derelict ship: metals and one technology fragment (GDD §7, used by #27). */
  derelict: { metals: 30, fragments: 1 },
  /** An anomaly: a burst of science. */
  anomaly: { science: 40 },
} as const;

/**
 * Picking a find (#105, a test of the author's idea of 21.10.2026): with the
 * lobby option on, a scan that ends in anything but pirates offers cards —
 * the drawn find and others the cloud's level can hold — and the player
 * takes one. Pirates still wake at once.
 */
export const FIND_CHOICE = {
  /** Cards offered. */
  cards: 3,
  /** Unpicked for this long (20 s), the first card — the drawn find — is taken. */
  timeoutTicks: 200,
} as const;

/**
 * Fog of war (#33, GDD §2): the star and the planets are in plain sight;
 * rival fleets are seen only within these radii (hexes) of the player's own
 * capital, fleets and outposts, and allies share what they see. A fleet
 * inside a dust cloud is seen only by fleets in the same cloud.
 */
export const VISION = {
  capital: 4,
  fleet: 2,
  /** Explorers carry sensors: they see further. */
  explorer: 3,
  /** Outposts see their whole zone of control and one hex beyond it. */
  outpostBeyondZone: 1,
  /** How often what players see is added to what they have explored (#97): once a second. */
  exploreEveryTicks: 10,
} as const;
